import { useEffect, useState } from "react";
import { api, timeAgo } from "@/lib/api";
import { ArticleCard, ArticleCardHero, ArticleCardSmall, SectionHeader } from "@/components/ArticleCards";
import AdSlot from "@/components/AdSlot";
import { Link } from "react-router-dom";
import { Flame, Sparkles, Calendar, GraduationCap, School, Building2 } from "lucide-react";
import AiAdvisorBanner from "@/components/AiAdvisorBanner";

export default function HomePage() {
  const [feed, setFeed] = useState({ all: [], breaking: [], trending: [], featured: [], editors: [], mostRead: [], events: [] });

  useEffect(() => {
    (async () => {
      const [all, breaking, trending, featured, editors, mostRead, events] = await Promise.all([
        api.get("/articles", { params: { limit: 30 } }),
        api.get("/articles", { params: { breaking: true, limit: 6 } }),
        api.get("/articles", { params: { trending: true, limit: 8 } }),
        api.get("/articles", { params: { featured: true, limit: 4 } }),
        api.get("/articles", { params: { editors_pick: true, limit: 6 } }),
        api.get("/articles/most-read", { params: { limit: 6 } }),
        api.get("/events", { params: { limit: 5 } }),
      ]);
      setFeed({
        all: all.data.items,
        breaking: breaking.data.items,
        trending: trending.data.items,
        featured: featured.data.items,
        editors: editors.data.items,
        mostRead: mostRead.data,
        events: events.data,
      });
    })();
  }, []);

  const hero = feed.featured[0] || feed.breaking[0] || feed.all[0];

  // De-duplication tracker across sections (including breaking-news ticker)
  const used = new Set();
  feed.breaking.forEach(a => a && used.add(a.slug || a.id));
  if (hero) used.add(hero.slug || hero.id);

  const take = (list, n) => {
    const out = [];
    for (const a of list) {
      if (!a) continue;
      const key = a.slug || a.id;
      if (used.has(key)) continue;
      used.add(key);
      out.push(a);
      if (out.length >= n) break;
    }
    return out;
  };

  const heroSide = take(feed.trending, 5);
  const latest = take(feed.all, 6);
  const trendingBlock = take(feed.trending, 4);
  const mostReadBlock = take(feed.mostRead, 5);
  const editorsBlock = take(feed.editors, 4);

  return (
    <div data-testid="home-page">
      {/* Breaking news ticker */}
      {feed.breaking.length > 0 && (
        <div className="bg-slate-900 text-white overflow-hidden" data-testid="breaking-ticker">
          <div className="max-w-7xl mx-auto flex items-center gap-4 px-4 py-2">
            <span className="overline text-[10px] px-3 py-1 rounded-full bg-red-500 shrink-0">Breaking</span>
            <div className="overflow-hidden flex-1">
              <div className="flex gap-10 animate-marquee whitespace-nowrap">
                {[...feed.breaking, ...feed.breaking].map((a, i) => (
                  <Link key={i} to={`/article/${a.slug}`} className="text-sm text-slate-200 hover:text-white">{a.title}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero grid — compact so more news is visible on first screen */}
      {/* Hero + Latest News together on the left; Advisor + Top Stories on the right */}
      <section className="max-w-7xl mx-auto px-4 pt-6 pb-14" data-testid="home-hero">
        <div className="grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-8 space-y-8">
            {hero && <ArticleCardHero article={hero} compact />}
            <div>
              <SectionHeader eyebrow="Latest News" title="Fresh from the Newsroom" viewAllTo="/category/latest" />
              <div className="grid sm:grid-cols-2 gap-6">
                {latest.map((a, i) => <ArticleCard key={a.id} article={a} tone={i % 2 ? "green" : "blue"} testId={`latest-card-${i}`} />)}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4" data-testid="hero-side-column">
            <AiAdvisorBanner compact />
            <div>
              <div className="overline text-sky-700 mb-2">Top Stories Now</div>
              <div className="flex flex-col gap-3">
                {heroSide.map(a => <ArticleCardSmall key={a.id} article={a} />)}
              </div>
            </div>
            {mostReadBlock.length > 0 && (
              <div className="card-soft p-5" data-testid="side-most-read">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-sky-700"/>
                  <div className="overline">Most Read</div>
                </div>
                <ol className="space-y-3">
                  {mostReadBlock.map((a, i) => (
                    <li key={a.id} className="flex gap-3 items-start">
                      <span className="font-editorial text-xl font-bold text-sky-700 leading-none">{i + 1}</span>
                      <Link to={`/article/${a.slug}`} className="font-editorial text-[14px] font-semibold text-slate-900 leading-snug title-link line-clamp-3">{a.title}</Link>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {/* Sidebar ad slot – renders only when an active ad is placed in Admin → Ads → Sidebar */}
            <AdSlot placement="sidebar"/>
          </div>
        </div>
      </section>

      {/* Top banner ad */}
      <AdSlot placement="home-top" className="max-w-7xl mx-auto px-4 pb-8"/>

      {/* Inline mid-feed ad */}
      <AdSlot placement="home-inline" className="max-w-7xl mx-auto px-4 pb-14"/>

      {/* Trending */}
      <section className="max-w-7xl mx-auto px-4 pb-14">
        <SectionHeader eyebrow="Trending" title="Stories moving fast" viewAllTo="/category/trending" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {trendingBlock.map((a, i) => (
            <div key={a.id} className="card-soft p-5 flex gap-4 hover-zoom">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-sm shrink-0">{i + 1}</div>
              <div className="flex-1">
                <Link to={`/article/${a.slug}`} className="font-editorial font-semibold text-slate-900 leading-snug title-link line-clamp-3 block">{a.title}</Link>
                <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1"><Flame className="w-3 h-3 text-emerald-600"/>{timeAgo(a.published_at)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Editor's picks - editorial style */}
      {editorsBlock.length > 0 && (
        <section className="bg-gradient-to-br from-sky-50/40 via-white to-emerald-50/40 border-y border-slate-100 py-14">
          <div className="max-w-7xl mx-auto px-4">
            <SectionHeader eyebrow="Editor's Picks" title="Handpicked reads worth your time" viewAllTo="/category/editors-picks" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {editorsBlock.map((a, i) => <ArticleCard key={a.id} article={a} tone={i % 2 ? "green" : "blue"} testId={`editors-pick-${i}`} />)}
            </div>
          </div>
        </section>
      )}

      {/* Category strips: University, School, EdTech, AI */}
      <section className="max-w-7xl mx-auto px-4 pt-10 pb-6 grid md:grid-cols-2 gap-10">
        <CategoryStrip label="University News" slug="university" icon={GraduationCap} tone="blue" used={used} />
        <CategoryStrip label="School News" slug="school" icon={School} tone="green" used={used} />
        <CategoryStrip label="EdTech" slug="edtech" icon={Building2} tone="blue" used={used} />
        <CategoryStrip label="AI in Education" slug="ai-in-education" icon={Sparkles} tone="green" used={used} />
      </section>

      {/* Events */}
      {feed.events.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-16">
          <SectionHeader eyebrow="Coming Up" title="Upcoming Events & Conferences" viewAllTo="/events" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {feed.events.slice(0, 3).map(e => (
              <div key={e.id} className="card-soft p-6" data-testid={`event-card-${e.id}`}>
                <div className="flex items-center gap-2 text-emerald-700 overline text-[10px] mb-3"><Calendar className="w-3.5 h-3.5"/>{e.type}</div>
                <h3 className="font-editorial text-xl font-bold text-slate-900 leading-snug">{e.title}</h3>
                <p className="text-sm text-slate-600 mt-2 line-clamp-2">{e.description}</p>
                <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center text-xs text-slate-500">
                  <span>{e.location}</span><span className="font-semibold text-sky-700">{e.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CategoryStrip({ label, slug, icon: Icon, tone, used }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/articles", { params: { category: slug, limit: 8 } }).then(r => setItems(r.data.items)); }, [slug]);
  // Filter out already-shown articles then take fresh ones
  const fresh = items.filter(a => !used || !used.has(a.slug || a.id));
  if (!fresh.length) return null;
  fresh.forEach(a => used && used.add(a.slug || a.id));
  const [lead, ...rest] = fresh;
  const tag = tone === "blue" ? "tag-blue" : "tag-green";
  return (
    <div data-testid={`category-strip-${slug}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg grid place-items-center ${tone === "blue" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}><Icon className="w-4 h-4"/></div>
          <h3 className="font-editorial text-xl font-bold text-slate-900">{label}</h3>
        </div>
        <Link to={`/category/${slug}`} className="text-xs font-semibold text-sky-700">More →</Link>
      </div>
      <Link to={`/article/${lead.slug}`} className="block hover-zoom mb-4">
        {lead.featured_image && (
          <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-3 bg-slate-100"><img src={lead.featured_image} alt={lead.title} className="w-full h-full object-cover"/></div>
        )}
        <span className={`overline text-[9px] px-2.5 py-1 rounded-full ${tag}`}>{slug.replace(/-/g, " ")}</span>
        <h4 className="font-editorial text-xl font-bold text-slate-900 leading-snug title-link mt-2">{lead.title}</h4>
      </Link>
      <div className="space-y-3 border-t border-slate-100 pt-3">
        {rest.slice(0, 3).map(r => (
          <Link key={r.id} to={`/article/${r.slug}`} className="block title-link">
            <div className="font-editorial font-semibold text-[15px] text-slate-900 leading-snug line-clamp-2">{r.title}</div>
            <div className="text-[11px] text-slate-500 mt-1">{timeAgo(r.published_at)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
